import { ChangeEvent, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { createDemandAdjustment, updateDemandAdjustment } from "../../services";
import {
  DemandAdjustment,
  DemandAdjustmentTargets,
  DemandAdjustmentType,
  DemandAdjustmentWriteRequest,
} from "../../types/demand-factor.type";
import {
  MAX_MULTIPLIER,
  MAX_NAME_LENGTH,
  MIN_MULTIPLIER,
  MULTIPLIER_STEP,
  TYPE_LABEL,
  TYPE_OPTIONS,
} from "./constants";
import {
  DemandFactorFormValues,
  hasErrors,
  initialFormValues,
  toWriteRequest,
  validateForm,
} from "./formValues";
import FormWarnings from "./FormWarnings";
import TargetFields from "./TargetFields";
import { DemandFactorLookups } from "./useDemandFactorLookups";

interface DemandFactorFormDialogProps {
  open: boolean;
  /** The row being edited; null to add a new one. */
  row: DemandAdjustment | null;
  canSelectBranch: boolean;
  ownBranchCode: string | null;
  lookups: DemandFactorLookups;
  enabledTypes: DemandAdjustmentType[] | null;
  onClose: () => void;
  onSaved: () => void;
}

const HALF_WIDTH = { xs: 12, md: 6 };
const QUARTER_WIDTH = { xs: 12, sm: 6, md: 3 };

const MULTIPLIER_INPUT_PROPS = {
  htmlInput: { min: MIN_MULTIPLIER, max: MAX_MULTIPLIER, step: MULTIPLIER_STEP },
};

const NAME_INPUT_PROPS = { htmlInput: { maxLength: MAX_NAME_LENGTH } };

/** Answers whether the API stored the row. */
const saveRow = async (
  row: DemandAdjustment | null,
  body: DemandAdjustmentWriteRequest,
): Promise<boolean> => {
  try {
    if (row) {
      await updateDemandAdjustment(row.id, body);
    } else {
      await createDemandAdjustment(body);
    }
    return true;
  } catch {
    // The axios interceptor already showed the API's Thai message; the dialog stays open on
    // the typed values so they can be corrected and saved again.
    return false;
  }
};

/** Adds or edits one Demand Factor row. */
export default function DemandFactorFormDialog({
  open,
  row,
  canSelectBranch,
  ownBranchCode,
  lookups,
  enabledTypes,
  onClose,
  onSaved,
}: DemandFactorFormDialogProps) {
  const [values, setValues] = useState<DemandFactorFormValues>(() =>
    initialFormValues(row, ownBranchCode),
  );
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    if (!open) return;
    setValues(initialFormValues(row, ownBranchCode));
    setSubmitted(false);
  }, [open, row, ownBranchCode]);

  const errors = validateForm(values, submitted);

  const updateValues = (patch: Partial<DemandFactorFormValues>) => {
    setValues((previous) => ({ ...previous, ...patch }));
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateValues({ name: event.target.value });
  };

  const handleTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const type = TYPE_OPTIONS.find((option) => option === event.target.value);
    updateValues({ type: type ?? "" });
  };

  const handleMultiplierChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateValues({ multiplier: event.target.value });
  };

  const handleValidFromChange = (validFrom: Dayjs | null) => {
    updateValues({ validFrom });
  };

  const handleValidToChange = (validTo: Dayjs | null) => {
    updateValues({ validTo });
  };

  const handleTargetsChange = (patch: Partial<DemandAdjustmentTargets>) => {
    setValues((previous) => ({ ...previous, targets: { ...previous.targets, ...patch } }));
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    const body = toWriteRequest(values);
    if (hasErrors(validateForm(values, true)) || !body) return;

    setSaving(true);
    const saved = await saveRow(row, body);
    setSaving(false);
    if (saved) onSaved();
  };

  const title = row ? "แก้ไข Demand Factor" : "เพิ่ม Demand Factor";

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
        <DialogContent dividers>
          <Stack gap={3}>
            <Grid container spacing={2}>
              <Grid size={HALF_WIDTH}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="ชื่อ"
                  value={values.name}
                  onChange={handleNameChange}
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                  slotProps={NAME_INPUT_PROPS}
                />
              </Grid>
              <Grid size={QUARTER_WIDTH}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  required
                  label="ประเภท"
                  value={values.type}
                  onChange={handleTypeChange}
                  error={Boolean(errors.type)}
                  helperText={errors.type}
                >
                  {TYPE_OPTIONS.map((type) => (
                    <MenuItem key={type} value={type}>
                      {TYPE_LABEL[type]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={QUARTER_WIDTH}>
                <TextField
                  type="number"
                  size="small"
                  fullWidth
                  required
                  label="ตัวคูณ"
                  value={values.multiplier}
                  onChange={handleMultiplierChange}
                  error={Boolean(errors.multiplier)}
                  helperText={errors.multiplier ?? `${MIN_MULTIPLIER} – ${MAX_MULTIPLIER}`}
                  slotProps={MULTIPLIER_INPUT_PROPS}
                />
              </Grid>
              <Grid size={HALF_WIDTH}>
                <DatePicker
                  label="วันที่เริ่ม"
                  value={values.validFrom}
                  format="DD/MM/YYYY"
                  onChange={handleValidFromChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: Boolean(errors.validFrom),
                      helperText: errors.validFrom,
                    },
                  }}
                />
              </Grid>
              <Grid size={HALF_WIDTH}>
                <DatePicker
                  label="วันที่สิ้นสุด"
                  value={values.validTo}
                  format="DD/MM/YYYY"
                  onChange={handleValidToChange}
                  minDate={values.validFrom ?? undefined}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      required: true,
                      error: Boolean(errors.validTo),
                      helperText: errors.validTo ?? "รวมวันสุดท้าย",
                    },
                  }}
                />
              </Grid>
            </Grid>
            <TargetFields
              targets={values.targets}
              onChange={handleTargetsChange}
              lookups={lookups}
              canSelectBranch={canSelectBranch}
              ownBranchCode={ownBranchCode}
            />
            <FormWarnings
              values={values}
              editingId={row?.id ?? null}
              enabledTypes={enabledTypes}
              names={lookups}
            />
          </Stack>
        </DialogContent>
      </LocalizationProvider>
      <DialogActions>
        <Button disabled={saving} onClick={handleClose}>
          ยกเลิก
        </Button>
        <Button loading={saving} variant="contained" onClick={handleSubmit}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
